import debug from '../utils/console'
import { createUUID } from '#imports'
import { once } from 'lodash-es'
import { OpenFeature, type Client } from '@openfeature/web-sdk'
import {
  COHORT_COOKIE,
  SPLIT_TEST_NAME_COOKIE,
  TARGETING_KEY_COOKIE,
} from '../openfeature/utils'
import { hasShiftHostApi } from '../utils/host-browser-apis/hasHostApi'

// Cohort names based on NATO phonetic alphabet
const COHORTS = [
  'alfa',
  'bravo',
  'charlie',
  'delta',
  'echo',
  'foxtrot',
  'golf',
  'hotel',
  'india',
  'juliett',
]

const logger = debug.withTag('openfeature')

const result = new Map<string, Client>()

let loading: Promise<Client> | null = null

async function initializeOpenFeature(): Promise<{
  client: Client
  loaded: Promise<Client>
}> {
  if (loading) {
    await loading
  }
  if (result.has('client') && result.get('client') !== undefined) {
    const existingClient = result.get('client')
    if (existingClient) {
      return {
        client: existingClient,
        loaded: Promise.resolve(existingClient),
      }
    }
  }
  const maxAge = 1000 * 60 * 60 * 24 * 365 * 10 // 10 years
  const targetingKey = useCookie(TARGETING_KEY_COOKIE, {
    maxAge,
  })
  targetingKey.value = targetingKey.value || createUUID()

  // Assign persistent cohort (0-9) on first visit
  const cohortCookie = useCookie(COHORT_COOKIE, { maxAge })
  if (!cohortCookie.value || !COHORTS.includes(cohortCookie.value)) {
    const key = String(targetingKey.value)
    const randomIndex =
      Array.from(key).reduce((sum, ch) => sum + ch.charCodeAt(0), 0) %
      COHORTS.length // 0 to COHORTS length
    cohortCookie.value = COHORTS[randomIndex]
  }

  const filters = useNuxtApp().$filters || {
    userAgent: null,
    isMobile: null,
  }

  const params = useRoute().query
  const rawUtmSource = params.utm_source
  const utmSource = Array.isArray(rawUtmSource) ? rawUtmSource[0] : rawUtmSource
  const isShiftBrowser =
    'isShiftBrowser' in params
      ? params.isShiftBrowser === 'true'
      : hasShiftHostApi()
  const splitTestNameCookie = useCookie(SPLIT_TEST_NAME_COOKIE, {
    maxAge,
  })

  const engagementData = userEngagementStore()

  // Update user context docs - https://redbrickmedia.atlassian.net/wiki/x/LwC63Q
  OpenFeature.setContext({
    ...filters, // CC mapping = custom.isMobile, custom.userAgent
    ...params, // CC mapping = query parameters (eg. siteSettings, enableGamesCategory, etc.)
    targetingKey: targetingKey.value, // CC mapping = identifier
    ...(splitTestNameCookie.value // CC mapping = custom.splittestname
      ? { splittestname: splitTestNameCookie.value }
      : {}),
    utmSource: utmSource || null, // CC mapping = custom.utm_source
    isEngaged: engagementData.isEngaged, // CC mapping = custom.isEngaged (plugin updates once loaded from localStorage)
    daysSinceInstall: engagementData.daysSinceInstall, // CC mapping = custom.daysSinceInstall
    installDate: Number(engagementData.installDate), // CC mapping = custom.installDate
    cohort: cohortCookie.value, // CC mapping = custom.cohort
    isShiftBrowser, // CC mapping = custom.isShiftBrowser
  })

  logger.log({ filters })

  const {
    registerMultipleFeatureFlagProviders,
    registerFeatureFlagProvider,
    teardown,
  } = await import('../openfeature/client.web')

  if (loading) {
    await loading
  }

  const { configCatSdkKey } = useRuntimeConfig().public
  const {
    openfeature = {
      multiProvider: false,
      localProvider: false,
      queryProvider: false,
    },
  } = useAppConfig()
  const {
    multiProvider = false,
    localProvider = false,
    queryProvider = false,
  } = openfeature

  const options = {
    configCatKey: configCatSdkKey ?? undefined,
    localProvider,
    queryProvider,
  }
  const openfeatureLoaded = multiProvider
    ? registerMultipleFeatureFlagProviders(options)
    : registerFeatureFlagProvider(options)

  loading = openfeatureLoaded
  const client = await openfeatureLoaded
  logger.log({ client, OpenFeature, context: OpenFeature.getContext() })
  let cleanupRegistered = false

  const splitTestName = client.getStringValue('splittestname', '')
  if (splitTestName) splitTestNameCookie.value = splitTestName

  OpenFeature.setContext({
    ...OpenFeature.getContext(),
    splittestname: splitTestNameCookie.value ?? '',
    cohort: cohortCookie.value ?? '',
    isShiftBrowser,
  })

  if (typeof window !== 'undefined') {
    if (!cleanupRegistered) {
      window.addEventListener('beforeunload', () => {
        logger.log('Teardown OpenFeature client on beforeunload')
        // Use sync teardown or fire-and-forget async
        teardown().catch((err) => logger.error('Teardown failed:', err))
      })
      cleanupRegistered = true
    }

    // Report cohort as ntp_cohort property to host browser analytics
    try {
      setProfileProperty('ntp_cohort', String(cohortCookie.value || ''))
    } catch (err) {
      logger.warn('Failed to set NTP cohort property for analytics', err)
    }
  }

  return {
    client,
    loaded: loading,
  }
}

export const useOpenFeature = once(initializeOpenFeature)
